import { ReactNode } from "react";

import AppLink from "@components/link/link";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

type StatusTone = "enabled" | "neutral" | "warning";

const STATUS_CLASS: Record<StatusTone, string> = {
  enabled: "text-emerald-600",
  neutral: "text-gray-500",
  warning: "text-amber-600",
};

type CommonProps = {
  name: string;
  logo: string;
  logoNode?: ReactNode;
  summary: ReactNode;
  statusLabel?: string;
  statusTone?: StatusTone;
  isLoading?: boolean;
};

type LinkProps = CommonProps & { to: string; onClick?: never };
type ButtonProps = CommonProps & { to?: never; onClick: () => void };

type IntegrationCardProps = LinkProps | ButtonProps;

const CardBody = ({
  name,
  logo,
  logoNode,
  summary,
  statusLabel,
  statusTone = "neutral",
}: CommonProps) => (
  <>
    <div className="flex items-start gap-4">
      <div className="h-7 w-7 flex-shrink-0 flex items-center justify-center">
        {logoNode ?? (
          <img
            src={getAssetUrl(logo)}
            alt=""
            className="h-full w-full object-contain"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900 truncate">
          {name}
        </div>
        {statusLabel && (
          <div
            className={classNames("text-xs mt-0.5", STATUS_CLASS[statusTone])}
          >
            {statusLabel}
          </div>
        )}
      </div>
    </div>
    <div className="text-sm text-gray-500 mt-3 line-clamp-3">{summary}</div>
  </>
);

const CARD_CLASS =
  "block text-left bg-white rounded-lg border border-gray-200 p-4 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm";

const IntegrationCard = (props: IntegrationCardProps) => {
  if ("to" in props && props.to) {
    return (
      <AppLink to={props.to} className={CARD_CLASS}>
        <CardBody {...props} />
      </AppLink>
    );
  }
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.isLoading}
      className={classNames(
        CARD_CLASS,
        "w-full disabled:opacity-60 disabled:cursor-not-allowed",
      )}
    >
      <CardBody {...props} />
    </button>
  );
};

export default IntegrationCard;
